import React from "react";
import { Check, ListChecks, X } from "lucide-react";
import type { WhereToStartListSummary } from "@atlas/shared";

// List picker for Where to start: a token input. The whole field is the
// control: selected lists are removable chips followed by a combobox input;
// focusing it opens a grouped checklist of lists. Toggling applies at once;
// the page owns the state (URL, localStorage, re-rank).

export interface WhereToStartListPickerProps {
  lists: WhereToStartListSummary[];
  selected: string[];
  onToggle: (slug: string) => void;
}

export function WhereToStartListPicker({ lists, selected, onToggle }: WhereToStartListPickerProps) {
  const [open, setOpen] = React.useState(false);
  const [focused, setFocused] = React.useState(false);
  const [q, setQ] = React.useState("");
  const [active, setActive] = React.useState(0);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const listId = React.useId();

  const nameOf = (slug: string) => lists.find((l) => l.slug === slug)?.name ?? slug;

  const groups = React.useMemo(() => {
    const needle = q.trim().toLowerCase();
    const sel = new Set(selected);
    const byRegion = new Map<string, WhereToStartListSummary[]>();
    for (const l of lists) {
      if (needle && !l.name.toLowerCase().includes(needle) && !(l.region ?? "").toLowerCase().includes(needle)) continue;
      const key = l.region?.trim() || "Other";
      byRegion.set(key, [...(byRegion.get(key) ?? []), l]);
    }
    const rank = (l: WhereToStartListSummary) => (sel.has(l.slug) ? 0 : l.started ? 1 : 2);
    return [...byRegion.entries()]
      .map(([region, items]) => ({
        region,
        selectedCount: items.filter((l) => sel.has(l.slug)).length,
        // Ticked first, then started; the server order is kept within each band.
        items: items
          .map((l, i) => ({ l, i }))
          .sort((a, b) => rank(a.l) - rank(b.l) || a.i - b.i)
          .map((x) => x.l),
      }))
      .sort((a, b) => b.selectedCount - a.selectedCount || a.region.localeCompare(b.region, "en-GB"));
  }, [lists, selected, q]);
  const flat = React.useMemo(() => groups.flatMap((g) => g.items), [groups]);

  React.useEffect(() => setActive(0), [q]);
  React.useEffect(() => {
    if (active >= flat.length) setActive(Math.max(0, flat.length - 1));
  }, [flat.length, active]);

  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQ("");
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const toggle = (slug: string) => {
    onToggle(slug);
    setQ("");
    inputRef.current?.focus();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      if (open) {
        e.preventDefault();
        setOpen(false);
        setQ("");
      }
    } else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      if (flat.length === 0) return;
      setActive((a) => (e.key === "ArrowDown" ? (a + 1) % flat.length : (a - 1 + flat.length) % flat.length));
    } else if (e.key === "Enter") {
      if (open && flat[active]) {
        e.preventDefault();
        toggle(flat[active].slug);
      }
    } else if (e.key === "Backspace" && q === "") {
      const last = selected[selected.length - 1];
      if (last) onToggle(last);
    }
  };

  const optId = (i: number) => `${listId}-opt-${i}`;
  let idx = -1;

  // Keep the active option in view while arrowing.
  React.useEffect(() => {
    if (!open) return;
    document.getElementById(optId(active))?.scrollIntoView?.({ block: "nearest" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, open]);

  return (
    <div
      ref={rootRef}
      style={{ position: "relative" }}
      onBlur={(e) => {
        if (!rootRef.current?.contains(e.relatedTarget as Node | null)) {
          setFocused(false);
          setOpen(false);
          setQ("");
        }
      }}
      onFocus={() => setFocused(true)}
    >
      <div
        onClick={() => inputRef.current?.focus()}
        style={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 6,
          minHeight: 44,
          padding: "5px 10px",
          background: "var(--surface-sunken)",
          border: `1px solid ${focused ? "var(--border-gold)" : "var(--border-default)"}`,
          boxShadow: focused ? "var(--focus-shadow)" : "none",
          borderRadius: 10,
          boxSizing: "border-box",
          cursor: "text",
        }}
      >
        <ListChecks size={16} aria-hidden="true" style={{ color: "var(--text-muted)", flexShrink: 0 }} />
        {selected.map((slug) => {
          const name = nameOf(slug);
          return (
            <span
              key={slug}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 2,
                height: 28,
                padding: "0 4px 0 10px",
                borderRadius: 999,
                background: "var(--accent-soft)",
                border: "1px solid var(--border-gold)",
                color: "var(--text-accent)",
                font: "600 13px var(--font-sans)",
                maxWidth: "100%",
              }}
            >
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</span>
              <button
                type="button"
                aria-label={`Remove ${name}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggle(slug);
                  inputRef.current?.focus();
                }}
                style={{ width: 22, height: 22, display: "grid", placeItems: "center", border: 0, borderRadius: "50%", background: "transparent", color: "inherit", cursor: "pointer", padding: 0 }}
              >
                <X size={13} aria-hidden="true" />
              </button>
            </span>
          );
        })}
        <input
          ref={inputRef}
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={open && flat.length > 0 ? optId(active) : undefined}
          aria-label="Lists that count"
          placeholder={selected.length === 0 ? "Pick a list" : "Add a list"}
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          onKeyDown={onKeyDown}
          autoComplete="off"
          spellCheck={false}
          style={{ flex: "1 1 90px", minWidth: 90, height: 30, background: "none", border: 0, outline: 0, boxShadow: "none", color: "var(--text-primary)", font: "inherit", fontSize: 14 }}
        />
      </div>

      {open && (
        <div
          id={listId}
          role="listbox"
          aria-label="Lists"
          aria-multiselectable="true"
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            top: "calc(100% + 6px)",
            zIndex: 30,
            background: "var(--surface-card)",
            border: "1px solid var(--border-strong)",
            borderRadius: 12,
            boxShadow: "var(--shadow-lg)",
            maxHeight: 380,
            overflowY: "auto",
            padding: 6,
          }}
        >
          {flat.length === 0 && (
            <div style={{ padding: "10px 8px", fontSize: 13, color: "var(--text-muted)" }}>No lists match.</div>
          )}
          {groups.map((g) => (
            <div key={g.region} role="group" aria-label={g.region}>
              <div className="eyebrow" style={{ fontSize: 10, padding: "10px 8px 4px" }}>
                {g.region}
              </div>
              {g.items.map((l) => {
                idx += 1;
                const i = idx;
                const on = selected.includes(l.slug);
                return (
                  <div
                    key={l.slug}
                    id={optId(i)}
                    role="option"
                    aria-selected={on}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      toggle(l.slug);
                    }}
                    onMouseEnter={() => setActive(i)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      minHeight: 44,
                      padding: "4px 8px",
                      boxSizing: "border-box",
                      borderRadius: 8,
                      cursor: "pointer",
                      fontSize: 14,
                      color: "var(--text-primary)",
                      background: i === active ? "var(--surface-raised)" : "transparent",
                    }}
                  >
                    <span
                      aria-hidden="true"
                      style={{
                        width: 18,
                        height: 18,
                        flex: "none",
                        display: "grid",
                        placeItems: "center",
                        borderRadius: 5,
                        border: `1px solid ${on ? "var(--accent)" : "var(--border-strong)"}`,
                        background: on ? "var(--accent)" : "transparent",
                        color: "var(--text-on-gold)",
                      }}
                    >
                      {on && <Check size={13} strokeWidth={3} />}
                    </span>
                    <span style={{ flex: 1, minWidth: 0, fontWeight: on ? 600 : 500 }}>{l.name}</span>
                    <span className="tnum" style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-muted)", whiteSpace: "nowrap" }}>
                      {l.remaining} left of {l.total}
                    </span>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
